import React, { useMemo } from "react";
import { motion } from "framer-motion";
import {
  Users,
  CheckCircle2,
  Clock,
  Sparkles,
  ArrowUpRight,
  Plus,
} from "lucide-react";
import { Link } from "react-router-dom";
import StatCard from "../../components/StatCard";
import {
  ChartCard,
  TimelineChart,
  StatusDonut,
  ProgramsBar,
} from "../../components/charts/Charts";
import {
  lastNDaysSeries,
  combineSeries,
  groupBy,
  topNFromObj,
  statusCounts,
  relativeTime,
} from "../../lib/chart-utils";

export default function RecruiterOverview({ me, apps }) {
  const tline = useMemo(() => lastNDaysSeries(apps, 7), [apps]);
  const tlData = useMemo(
    () => combineSeries([{ key: "Referrals", data: tline }]),
    [tline]
  );
  const programData = useMemo(() => {
    const grouped = groupBy(apps || [], (a) =>
      (a.program || "Unspecified").split(" \u2014 ")[0]
    );
    return topNFromObj(grouped, 6);
  }, [apps]);
  const statusData = useMemo(() => statusCounts(apps || []), [apps]);

  const stats = {
    total: apps.length,
    submitted: apps.filter((a) => (a.status || "submitted") === "submitted")
      .length,
    week: tline.reduce((s, d) => s + d.value, 0),
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#2A4FAE] text-white p-6 sm:p-8"
      >
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#C49A3A]/30 blur-3xl" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase font-semibold text-amber-300">
              <Sparkles className="w-3.5 h-3.5" /> Partner Dashboard
            </div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold">
              Welcome, {me?.full_name?.split(" ")[0]}
            </h2>
            <p className="mt-1 text-white/75 max-w-xl text-sm">
              {me?.company_name} · Account status:{" "}
              <span className="text-amber-300 font-semibold capitalize">
                {me?.status}
              </span>
            </p>
          </div>
          <Link
            to="/recruiter/dashboard/new"
            className="inline-flex items-center gap-2 bg-white text-cct-blue text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-amber-50 self-start"
          >
            <Plus className="w-4 h-4" /> Refer Student
          </Link>
        </div>
      </motion.div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          icon={Users}
          label="Total Referrals"
          value={stats.total}
          accent="#1E3A8A"
          spark={tline.map((d) => d.value)}
          delay={0.05}
        />
        <StatCard
          icon={Clock}
          label="This Week"
          value={stats.week}
          accent="#C49A3A"
          spark={tline.map((d) => d.value)}
          delay={0.1}
        />
        <StatCard
          icon={CheckCircle2}
          label="Submitted"
          value={stats.submitted}
          accent="#059669"
          delay={0.15}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <ChartCard
            title="Referrals — Last 7 days"
            subtitle="Daily volume trend"
            delay={0.2}
          >
            <TimelineChart
              data={tlData}
              series={["Referrals"]}
              colors={["#1E3A8A"]}
            />
          </ChartCard>
        </div>
        <ChartCard
          title="Referral Status"
          subtitle="Distribution"
          delay={0.25}
        >
          <StatusDonut data={statusData} />
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <ChartCard
            title="Top Programs"
            subtitle="Most-requested by your students"
            delay={0.3}
          >
            <ProgramsBar data={programData} />
          </ChartCard>
        </div>
        <ChartCard
          title="Recent Referrals"
          subtitle="Latest student applications"
          delay={0.35}
          action={
            <Link
              to="/recruiter/dashboard/referrals"
              className="text-xs font-semibold text-cct-blue hover:underline inline-flex items-center gap-1"
            >
              All <ArrowUpRight className="w-3 h-3" />
            </Link>
          }
        >
          {apps.length === 0 ? (
            <div className="text-sm text-slate-400 py-6 text-center">
              No referrals yet
            </div>
          ) : (
            <div className="space-y-1">
              {apps.slice(0, 6).map((a, i) => (
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, x: 8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + i * 0.04 }}
                  className="flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-slate-50"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1E3A8A] to-[#3B5BC9] text-white flex items-center justify-center font-semibold text-xs">
                    {a.full_name?.slice(0, 1)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800 truncate">
                      {a.full_name}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {a.program || "No program"}
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-400 whitespace-nowrap">
                    {relativeTime(a.created_at)}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </ChartCard>
      </div>
    </div>
  );
}
